import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

export interface InventoryReportCommand {
  startDate: Date;
  endDate: Date;
  includeCosts?: boolean;
  timezoneOffset?: number;
  locationCode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class InventoryReportProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/report';
  private readonly balanceBaseUrl = environment.proxyUrls.inventoryApi + '/api/v1/inventory-balance';
  private readonly consumableBalanceBaseUrl = environment.proxyUrls.inventoryApi + '/api/v1/consumable-balance';

  /**
   * Returns the machine's UTC offset in minutes using the API convention
   * (UTC-6 => -360). JavaScript's `getTimezoneOffset()` returns the inverse,
   * so we negate it.
   */
  private getLocalOffsetMinutes(): number {
    return -new Date().getTimezoneOffset();
  }

  /**
   * Serializes a Date as an ISO 8601 string that preserves the local wall-clock
   * time and includes the machine's timezone offset (e.g. "2026-04-16T00:00:00-06:00").
   * This avoids the UTC shift caused by `Date.toISOString()`.
   */
  private toLocalIsoString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const offsetMinutes = this.getLocalOffsetMinutes();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMinutes);
    const offsetHours = pad(Math.floor(absOffset / 60));
    const offsetMins = pad(absOffset % 60);
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
      `${sign}${offsetHours}:${offsetMins}`
    );
  }

  public requisitionReport(command: InventoryReportCommand): Observable<Blob> {
    const offset = command.timezoneOffset ?? this.getLocalOffsetMinutes();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      startDate: this.toLocalIsoString(command.startDate),
      endDate: this.toLocalIsoString(command.endDate),
      timezoneOffset: offset.toString(),
    };
    if (command.includeCosts !== undefined) {
      params.includeCosts = command.includeCosts.toString();
    }
    return this.httpClient.get(`${this.baseUrl}/requisition-report`, { params, responseType: 'blob' });
  }

  public knittingInventoryBalanceReport(excludeZeroBalances: boolean, includeCosts: boolean): Observable<Blob> {
    const params = {
      excludeZeroBalances: excludeZeroBalances.toString(),
      includeCosts: includeCosts.toString(),
    };
    return this.httpClient.get(`${this.baseUrl}/knitting-inventory-balance-report`, { params, responseType: 'blob' });
  }

  public transferUnitReport(command: InventoryReportCommand): Observable<Blob> {
    const offset = command.timezoneOffset ?? this.getLocalOffsetMinutes();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      startDate: this.toLocalIsoString(command.startDate),
      endDate: this.toLocalIsoString(command.endDate),
      timezoneOffset: offset.toString(),
    };
    if (command.includeCosts !== undefined) {
      params.includeCosts = command.includeCosts.toString();
    }
    return this.httpClient.get(`${this.baseUrl}/transfer-units`, { params, responseType: 'blob' });
  }

  public guatemalaGreigeMovementReport(command: InventoryReportCommand): Observable<Blob> {
    const offset = command.timezoneOffset ?? this.getLocalOffsetMinutes();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      startDate: this.toLocalIsoString(command.startDate),
      endDate: this.toLocalIsoString(command.endDate),
      timezoneOffset: offset.toString(),
    };
    if (command.includeCosts !== undefined) {
      params.includeCosts = command.includeCosts.toString();
    }
    return this.httpClient.get(`${this.baseUrl}/guatemala-greige-movement-report`, { params, responseType: 'blob' });
  }

  public consumableInboundReport(command: InventoryReportCommand): Observable<Blob> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      startDate: this.toLocalIsoString(command.startDate),
      endDate: this.toLocalIsoString(command.endDate),
      timezoneOffset: (command.timezoneOffset ?? this.getLocalOffsetMinutes()).toString(),
    };
    if (command.locationCode) {
      params.locationCode = command.locationCode;
    }
    return this.httpClient.get(`${this.baseUrl}/consumable/inbound`, { params, responseType: 'blob' });
  }

  public consumableOutboundReport(command: InventoryReportCommand): Observable<Blob> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      startDate: this.toLocalIsoString(command.startDate),
      endDate: this.toLocalIsoString(command.endDate),
      timezoneOffset: (command.timezoneOffset ?? this.getLocalOffsetMinutes()).toString(),
    };
    if (command.locationCode) {
      params.locationCode = command.locationCode;
    }
    return this.httpClient.get(`${this.baseUrl}/consumable/outbound`, { params, responseType: 'blob' });
  }

  public consumableBalanceReport(): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/consumable/balance-report`, { responseType: 'blob' });
  }

  /**
   * PBI 4659 — historical consumable balance (Repuestos/Químicos), as-of-date + 4-sheet Excel
   * export (INITIAL/IN/OUT/FINAL BALANCE). Replaces consumableBalanceReport() in the Inventory
   * Reports UI; that live-balance endpoint stays wired for reference but is no longer shown here.
   */
  public consumableHistoricalBalanceExport(warehouseCode: string, asOfDate: Date): Observable<Blob> {
    // The datepicker seeds the control with `new Date()` and keeps the residual seconds when only
    // the date and hour are changed, so picking 07:00 sends 07:00:46 — enough to land past a
    // month's close instant and come back with an empty delta.
    const instant = new Date(asOfDate);
    instant.setSeconds(0, 0);

    const params = {
      warehouseCode,
      asOfDate: this.toLocalIsoString(instant),
      timezoneOffset: this.getLocalOffsetMinutes().toString(),
    };
    return this.httpClient.get(`${this.consumableBalanceBaseUrl}/historical/export`, { params, responseType: 'blob' });
  }

  /**
   * PBI 4659 follow-up — closed-month consumable balance export (INITIAL/IN/OUT/FINAL BALANCE),
   * mirrors closedMonthBalanceExport() (Rolls). Reads snapshots directly by year/month instead of
   * an asOfDate, so there's no risk of landing on the wrong side of a snapshot's cutoff instant.
   */
  public consumableClosedMonthBalanceExport(warehouseCode: string, year: number, month: number): Observable<Blob> {
    const params = {
      warehouseCode,
      year: year.toString(),
      month: month.toString(),
    };
    return this.httpClient.get(`${this.consumableBalanceBaseUrl}/closed-month/export`, { params, responseType: 'blob' });
  }

  public productionTransferReport(
    lotItemNumber: string,
    entityType = 'lot-item',
    transferType?: string,
    timezoneOffset = this.getLocalOffsetMinutes(),
  ): Observable<Blob> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      entityType: entityType,
      timezoneOffset: timezoneOffset,
    };
    if (transferType) {
      params.transferType = transferType;
    }
    return this.httpClient.get(`${this.baseUrl}/production-transfer/${encodeURIComponent(lotItemNumber)}`, {
      params,
      responseType: 'blob',
    });
  }

  public warehouseMovementReport(locationCode: string, startDate: Date, endDate: Date): Observable<Blob> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      locationCode: locationCode,
      startDate: this.toLocalIsoString(startDate),
      endDate: this.toLocalIsoString(endDate),
      timezoneOffset: this.getLocalOffsetMinutes().toString(),
    };
    return this.httpClient.get(`${this.baseUrl}/warehouse-movement`, { params, responseType: 'blob' });
  }

  public historicalBalanceExport(locationCode: string, asOfDate: Date, includeCost = false): Observable<Blob> {
    const params = {
      locationCode,
      asOfDate: this.toLocalIsoString(asOfDate),
      timezoneOffset: this.getLocalOffsetMinutes().toString(),
      detailed: 'true',
      includeCost: includeCost.toString(),
    };
    return this.httpClient.get(`${this.balanceBaseUrl}/historical/export`, { params, responseType: 'blob' });
  }

  public closedMonthBalanceExport(locationCode: string, year: number, month: number, includeCost = false): Observable<Blob> {
    const params = {
      locationCode,
      year: year.toString(),
      month: month.toString(),
      includeCost: includeCost.toString(),
    };
    return this.httpClient.get(`${this.balanceBaseUrl}/closed-month/export`, { params, responseType: 'blob' });
  }

  public monthlyBillingReport(startDate: Date, endDate: Date, includePrices = false): Observable<Blob> {
    const params = {
      startDate: this.toLocalIsoString(startDate),
      endDate: this.toLocalIsoString(endDate),
      includePrices: includePrices.toString(),
    };
    return this.httpClient.get(`${this.baseUrl}/monthly-billing`, { params, responseType: 'blob' });
  }

  public generateRemissionGuideReport(remissionGuideId: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/remission-guide/${remissionGuideId}`, { responseType: 'blob' });
  }
}
